<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import ListingPagination from '@/components/listing-pagination'
import i18n from '@/i18n'
import { mapState } from 'vuex'
import { SESSION, SHARED } from '@/constants'
import { sessionHelper, ddlHelper, sharedHelper } from '@/utils'
import { ORGANIZATION_MODULE } from '@/state/modules/organization'
import { organizationMethods } from '@/state/helpers'

/**
 * Organisations — the brands whose players agents answer.
 *
 * ── What this screen cannot do, and why ─────────────────────────────────
 * ai_bot owns the tenant registry; the local `tenants` table is a mirror. So:
 *
 *   no rename   ai_bot's API lists `name` as operator-only and refuses it, and
 *               a local rename would be reverted by the next sync. Offering a
 *               field that silently does nothing is worse than not offering it.
 *   no delete   conversations, messages and contacts all hang off tenant_id.
 *   switch off  means "the inbox stops taking work for this brand". It does
 *               NOT take the client's chatbot offline. Said in the dialog,
 *               because the two are easy to confuse and only one is reversible
 *               without a phone call.
 */
export default {
  page: { title: i18n.t('label.listingX', [i18n.t('module.organization')]) },
  components: { Layout, PageHeader, MultiSelect, ListingPagination },
  data() {
    return {
      title: i18n.t('label.listingX', [i18n.t('module.organization')]),
      breadcrumbItems: [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.organization'), active: true },
      ],
      statusDdl: ddlHelper.getActivationStatusMulti(),
      filter: '',
      searchCriteria: {},
      showFilters: false,
      fields: [
        { key: 'tenantId', sortable: true, label: i18n.t('organization.tenantId') },
        { key: 'name', sortable: true, label: i18n.t('label.name') },
        { key: 'bot', sortable: false, label: i18n.t('organization.botSkills') },
        { key: 'agentCount', sortable: false, label: i18n.t('organization.agents') },
        { key: 'conversationCount', sortable: false, label: i18n.t('module.inbox') },
        { key: 'isActive', sortable: false, label: i18n.t('organization.inboxStatus') },
        { key: 'syncedAt', sortable: false, label: i18n.t('organization.lastSynced') },
        { key: 'action', sortable: false, label: i18n.t('label.action') },
      ],
    }
  },
  computed: {
    ...mapState(ORGANIZATION_MODULE, {
      tableRows: (state) => state.list.data,
      pagination: (state) => state.list.pagination,
      status: (state) => state.status,
    }),
    tenantDdl() {
      return this.tableRows.map((r) => ({ text: `${r.tenantId} — ${r.name}`, value: r.tenantId }))
    },
    activeFilterCount() {
      const c = this.searchCriteria
      return [c.isActive, c.tenantIds].filter((v) => Array.isArray(v) && v.length > 0).length
    },
  },
  created() {
    this.initSearchCriteria()
  },
  methods: {
    ...organizationMethods,

    initSearchCriteria() {
      if (sessionHelper.hasSearchCriteria(SESSION.ORGANIZATIONS)) {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.ORGANIZATIONS)
        this.filter = (this.searchCriteria.searchArray || [])[0] || ''
        this.showFilters = this.activeFilterCount > 0
      } else {
        this.searchCriteria = this.defaultCriteria()
      }

      this.getList(this.searchCriteria)
    },

    defaultCriteria() {
      return {
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        pageSize: SHARED.DEFAULT_PAGE_SIZE,
        searchArray: [],
        isActive: [],
        tenantIds: [],
        // On by default here: this screen's whole subject is what the bot is
        // doing for each brand, so a page without it has little to show. It is
        // one HTTP call to ai_bot and a null answer is reported as unreachable
        // rather than as "no skills".
        withBot: true,
      }
    },

    getList(filter) {
      this.getAllOrganization({ filter })
      this.searchCriteria = filter
      sessionHelper.storeSearch(SESSION.ORGANIZATIONS, filter)
    },

    pageNavigation(pageNumber) {
      this.getList({ ...this.searchCriteria, pageNumber })
    },

    searchListing() {
      this.getList({
        ...this.searchCriteria,
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        searchArray: this.filter ? [this.filter] : [],
      })
    },

    resetFilters() {
      this.filter = ''
      this.getList(this.defaultCriteria())
    },

    openBrand(row) {
      this.$router.push(`/organizations/${row.tenantId}`)
    },

    async toggleStatus(row) {
      const ok = row.isActive
        ? await sharedHelper.confirmQuestion({
            title: i18n.t('organization.switchOffTitle', [row.name]),
            // Spells out the half that is NOT affected. Somebody switching a
            // brand off here is usually worried about the customer's website,
            // and this is the sentence that answers that.
            text: i18n.t('organization.switchOffConfirm'),
            confirmText: i18n.t('organization.switchOff'),
            cancelText: i18n.t('label.cancel'),
          })
        : true

      if (!ok) return

      this.updateOrganizationStatus({
        id: row.tenantId,
        isActive: !row.isActive,
        filter: this.searchCriteria,
      })
    },

    goCreate() {
      this.$router.push({ name: 'Organization-create' })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <b-alert show variant="info" class="small d-flex align-items-start">
          <i class="mdi mdi-information-outline font-size-16 mr-2"></i>
          <span>{{ $t('organization.mirrorNotice') }}</span>
        </b-alert>

        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center w-100">
                  {{ $t('label.search') }}
                  <b-form-input
                    v-model="filter"
                    type="search"
                    :placeholder="$t('organization.searchPlaceholder')"
                    class="form-control form-control-sm ml-2"
                    @change="searchListing"
                  ></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showFilters = !showFilters">
                  <i class="mdi mdi-filter-variant mr-1"></i>{{ $t('label.filter') }}
                  <b-badge v-if="activeFilterCount" variant="primary" class="ml-1">{{ activeFilterCount }}</b-badge>
                </b-button>
                <b-button v-if="$can('Create Organisation')" variant="primary" size="sm" @click="goCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.organization')]) }}
                </b-button>
              </div>
            </div>

            <b-collapse v-model="showFilters">
              <div class="border rounded p-3 mb-3 bg-light">
                <b-row>
                  <b-col cols="12" md="4" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('organization.inboxStatus') }}</label>
                    <MultiSelect v-model="searchCriteria.isActive" :options="statusDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" md="8" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('module.organization') }}</label>
                    <MultiSelect v-model="searchCriteria.tenantIds" :options="tenantDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" class="text-right">
                    <b-button variant="link" size="sm" @click="resetFilters">
                      {{ $t('label.clearFilters') }}
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </b-collapse>

            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :busy="!status.list" show-empty>
                <template #empty>
                  <p class="text-muted text-center my-3">{{ $t('label.noRecordFound') }}</p>
                </template>

                <template #cell(name)="data">
                  <a href="javascript:void(0);" class="font-weight-bold" @click="openBrand(data.item)">
                    {{ data.item.name }}
                  </a>
                </template>

                <template #cell(tenantId)="data">
                  <code>{{ data.item.tenantId }}</code>
                </template>

                <template #cell(bot)="data">
                  <!-- Four distinct answers. Conflating any two of them is
                       the bug this cell exists to avoid — an unreachable bot
                       used to render as a neutral dash, which reads as
                       "fine". See the store's mapOrganizationRow. -->
                  <span v-if="!data.item.botAsked" class="text-muted small">—</span>
                  <span v-else-if="!data.item.botAvailable" class="text-danger small">
                    <i class="mdi mdi-cloud-off-outline mr-1"></i>{{ $t('organization.botUnreachable') }}
                  </span>
                  <span v-else-if="data.item.bot === null" class="text-danger small">
                    <i class="mdi mdi-alert-outline mr-1"></i>{{ $t('organization.notInBot') }}
                  </span>
                  <span v-else-if="!data.item.botSkills.length" class="text-warning small">
                    {{ $t('organization.noSkills') }}
                  </span>
                  <template v-else>
                    <b-badge v-for="s in data.item.botSkills" :key="s" variant="light" class="mr-1 border">
                      {{ s }}
                    </b-badge>
                  </template>
                </template>

                <template #cell(agentCount)="data">
                  <span class="small">{{ data.item.agentCount }}</span>
                </template>

                <template #cell(conversationCount)="data">
                  <span class="small">{{ data.item.conversationCount }}</span>
                </template>

                <template #cell(isActive)="data">
                  <b-badge :variant="data.item.isActive ? 'success' : 'secondary'">
                    {{ data.item.isActive ? $t('label.active') : $t('label.inactive') }}
                  </b-badge>
                </template>

                <template #cell(syncedAt)="data">
                  <span v-if="data.item.isSynced" class="small text-muted">{{ data.item.syncedAt }}</span>
                  <!-- Never synced: the name shown is whatever created the row,
                       which is worth knowing before anyone calls it wrong. -->
                  <b-badge v-else variant="warning">{{ $t('organization.neverSynced') }}</b-badge>
                </template>

                <template #cell(action)="data">
                  <!-- One way in. The embed snippet, the backend credentials,
                       what the bot says when it is stuck and what the month has
                       cost all live on the brand's own page — four dialogs on a
                       listing is four places to look for "set this client up". -->
                  <a
                    href="javascript:void(0);"
                    class="text-primary mr-2"
                    v-b-tooltip.hover
                    :title="$t('organization.manage')"
                    @click="openBrand(data.item)"
                  >
                    <i class="mdi mdi-cog-outline font-size-18"></i>
                  </a>
                  <a
                    v-if="$can('Update Organisation')"
                    href="javascript:void(0);"
                    class="text-warning"
                    v-b-tooltip.hover
                    :title="$t('organization.inboxStatus')"
                    @click="toggleStatus(data.item)"
                  >
                    <i class="mdi mdi-power font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>

            <ListingPagination :pagination="pagination" @change="pageNavigation" />

          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.embed-code {
  background: #f7f8fb;
  border: 1px solid #eef0f4;
  border-radius: 6px;
  padding: 12px;
  font-size: 12.5px;
  white-space: pre-wrap;
  word-break: break-all;
  margin-bottom: 12px;
}
</style>
